import { useQuery } from "@tanstack/react-query";
import React from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Alert, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import ReactApexChart from "react-apexcharts";
import DateUtils from "src/services/utils/DateUtils";
import {
    EMPTY_LIST,
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import CreditHistory from "../kyb-common-components/history";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import { KYBCreditTypes } from "src/modules/3rd-party-management/constants";

const LoadingContent = () => {
    return (
        <div className="overview-tab">
            <Row className="mb-3">
                <Col lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                </Col>
                <Col lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                </Col>
                <Col lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                </Col>
            </Row>
            <Row>
                <Col lg="12">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '240px' }}></div>
                </Col>
            </Row>
        </div>
    )
}

const Dnb = ({ t }) => {
    const dateUtils = new DateUtils();

    const {
        data: overviewData,
        isFetching: isFetchingOverview
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-organization-license-overview',
            'dnb'
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchkYBLicenseDetail({
                provider: 'dun_brad_street'
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: isFeatureEnabledForActiveModule('kyb_activation'),
        onError: () => {
            toast(t('An error occurred while fetching overview content.'), {
                type: 'error'
            });
        },
    });

    if (!isFeatureEnabledForActiveModule('kyb_activation')) {
        return (
            <Alert
                color="warning"
            >
                <p className="mb-0">
                    {t("Please upgrade your plan to access this feature")}.
                </p>
            </Alert>
        )
    }

    if (isFetchingOverview) return <LoadingContent />;

    const { usageCounts = {}, totalCredits, creditTransactions } = overviewData || {};

    let totalUsage = 0;

    Object.keys(usageCounts).forEach((usagekey) => {
        totalUsage += usageCounts[usagekey].usageCount;
    })

    const remaining = totalCredits - totalUsage;
    const usedPercent = totalCredits > 0 ? ((totalUsage / totalCredits) * 100) : 0;
    const remainingPercent = totalCredits > 0 ? ((remaining / totalCredits) * 100) : 0;

    return (
        <div className="overview-tab">
            <Row>
                <Col sm="12" md="4">
                    <Card className="report-statistics-card">
                        <CardBody>
                            <Row>
                                <Col sm="6" md="8">
                                    <div className="overflow-hidden">
                                        <p className="text-truncate">
                                            {t('Total {{creditsLabel}}', {
                                                creditsLabel: 'Credits'
                                            })}
                                        </p>
                                        <h4 className="mb-0">
                                            {totalCredits}
                                        </h4>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                <Col sm="12" md="4">
                    <Card className="report-statistics-card">
                        <CardBody>
                            <Row>
                                <Col sm="6" md="8">
                                    <div className="overflow-hidden">
                                        <p className="text-truncate">
                                            {t('Used {{creditsLabel}}', {
                                                creditsLabel: 'Credits'
                                            })}
                                        </p>
                                        <h4 className="mb-0">
                                            {totalUsage}
                                        </h4>
                                    </div>
                                </Col>

                                <Col sm="6" md="4">
                                    <ReactApexChart
                                        options={{
                                            chart: {
                                                sparkline: {
                                                    enabled: true
                                                }
                                            },
                                            dataLabels: {
                                                enabled: true
                                            },
                                            colors: ['#FF4949'],
                                            stroke: {
                                                lineCap: 'round'
                                            },
                                            plotOptions: {
                                                radialBar: {
                                                    hollow: {
                                                        margin: 0,
                                                        size: '70%'
                                                    },
                                                    track: {
                                                        margin: 0,
                                                    },
                                                    dataLabels: {
                                                        show: true,
                                                        name: {
                                                            show: false
                                                        },
                                                        value: {
                                                            show: true,
                                                            offsetY: 6
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        series={[usedPercent.toFixed(1)]}
                                        type="radialBar"
                                        height="60"
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                <Col sm="12" md="4">
                    <Card className="report-statistics-card">
                        <CardBody>
                            <Row>
                                <Col sm="6" md="8">
                                    <div className="overflow-hidden">
                                        <p className="text-truncate">
                                            {t('Remaining {{creditsLabel}}', {
                                                creditsLabel: 'Credits'
                                            })}
                                        </p>
                                        <h4 className="mb-0">
                                            {remaining.toLocaleString()}
                                        </h4>
                                    </div>
                                </Col>

                                <Col sm="6" md="4">
                                    <ReactApexChart
                                        options={{
                                            chart: {
                                                sparkline: {
                                                    enabled: true
                                                }
                                            },
                                            dataLabels: {
                                                enabled: true
                                            },
                                            colors: ['#FF4949'],
                                            stroke: {
                                                lineCap: 'round'
                                            },
                                            plotOptions: {
                                                radialBar: {
                                                    hollow: {
                                                        margin: 0,
                                                        size: '70%'
                                                    },
                                                    track: {
                                                        margin: 0,
                                                    },
                                                    dataLabels: {
                                                        show: true,
                                                        name: {
                                                            show: false
                                                        },
                                                        value: {
                                                            show: true,
                                                            offsetY: 6
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        series={[remainingPercent.toFixed(1)]}
                                        type="radialBar"
                                        height="60"
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                
                <Col sm="12" md="12">
                    <Card>
                        <CardBody>
                            <CardTitle tag="h4">
                                {t('{{creditLabel}} Transactions', {
                                    creditLabel: 'Credit'
                                })}
                            </CardTitle>
                            <Table responsive bordered>
                                <thead>
                                    <tr>
                                        <th>{t('Transaction ID')}</th>
                                        <th>Credit</th>
                                        <th>{t('Type')}</th>
                                        <th>{t('Note')}</th>
                                        <th>{t('Date')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creditTransactions.length > 0 ? (
                                        creditTransactions.map((tx) => (
                                            <tr key={tx.id}>
                                                <td>
                                                    {tx.id}
                                                </td>
                                                <td>
                                                    {tx.licenseCount.toLocaleString()}
                                                </td>
                                                <td>
                                                    {t(KYBCreditTypes[tx.data_type]?.title)}
                                                </td>
                                                <td title={tx.note}>
                                                    {tx.note}
                                                </td>
                                                <td>
                                                    {dateUtils.convertDateToDate(tx.createdAt, INTERNATIONAL_DATE_FORMAT)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">
                                                {t(EMPTY_LIST)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>

                {/* <Col sm="12" md="12">
                    <CreditHistory />
                </Col> */}
            </Row>
        </div>
    );
};

export default withNamespaces()(
    Dnb
);
